/**
 * SlateChip — "who are we knocking for", visible for the whole walk.
 *
 * Tier 3 of the five surfaces in plan §8.2. The briefing (tier 1) tells a
 * volunteer the slate once, at the start; this is what keeps telling them
 * while they walk. It exists because of plan §0.3.2: canvassing here is
 * unpaid volunteer labor, and volunteers churn, skip briefings, and cannot be
 * compelled to train. Candidate identity has to be unmissable *at the moment
 * of use*, not taught once and forgotten.
 *
 * Three rules this component is built around
 * ------------------------------------------
 *
 * 1. **It never fetches.** The slate arrives on the `ShiftBriefing` payload
 *    and is passed in. `canvassOfflineQueue.ts` opens with the operative fact:
 *    "A canvasser standing on a rural porch in Platte County will lose LTE at
 *    least once per shift." A chip that fetched on render would be a blank
 *    chip exactly when it matters. Props in, no effects, no client.
 *
 * 2. **Empty is loud.** `candidates: []` is a *measured, expected* outcome —
 *    171 of 7,431 routes have no `precinct_num` to derive from, and every
 *    route created before candidates existed derives empty. So emptiness
 *    renders as an explicit warning, never as `null` and never as a chip with
 *    nothing in it. A volunteer who sees nothing must know it's missing rather
 *    than assume there's nothing to see. (This repo already shipped one
 *    dead-honesty bug — the lasso footer asserting "0 missing coordinates"
 *    when the count could never be non-zero. Not again.)
 *
 * 3. **375px is the real target, not a nice-to-have.** Field canvassers are on
 *    phones. Three "Name — Office" pairs do not fit in a phone-width header,
 *    so narrow gets party-colored initials + a count and taps to expand; wide
 *    gets the names inline. The structural split goes through `useMediaQuery`
 *    rather than CSS because the two render genuinely different subtrees.
 *
 * ⚠️ The AppShell header had a `min-w-0` truncation bug fixed during the
 * responsive work (`project_responsive_mobile`), and a header chip walks
 * straight back into that blast radius. Every flex child in here that can hold
 * text carries `min-w-0`, and the expanded panel is an overlay rather than an
 * inline grow — a chip that widens its own header row is how that bug comes
 * back. Verify at 375px specifically.
 */
import React, { useEffect, useRef, useState } from 'react';

import { useMediaQuery } from '@/hooks/useMediaQuery';
import type { CandidateBrief } from '@/types/candidate';

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

/**
 * Party → dot color. Deliberately NOT the app's red data-ramp: on this surface
 * red means "Republican", not "high value". Mirrors the `PARTY_PALETTE`
 * convention DataChangesView uses, kept local because that palette is a chart
 * scale and this is a UI affordance — sharing it would couple a legend to a
 * badge.
 */
function partyDotClass(party: string | null): string {
  const p = (party || '').trim().toLowerCase();
  if (p.startsWith('rep')) return 'bg-red-500';
  if (p.startsWith('dem')) return 'bg-blue-500';
  if (p.startsWith('lib')) return 'bg-yellow-500';
  if (p.startsWith('gre')) return 'bg-green-500';
  if (!p) return 'bg-fg-muted';
  return 'bg-purple-500';
}

/** The short name a volunteer says out loud; `display_name` wins when set. */
function shortName(c: CandidateBrief): string {
  return (c.display_name || '').trim() || c.full_name;
}

/**
 * Two-letter initials for the narrow chip: "Jane Smith" → "SM".
 *
 * Surname-first, because `display_name` is already the surname form ("Smith")
 * and a volunteer scanning a chip is matching it to the name in their mouth.
 * Falls back to the first two characters of a single-token name.
 */
function initialsOf(c: CandidateBrief): string {
  const name = shortName(c).trim();
  if (!name) return '??';
  const tokens = name.split(/\s+/).filter(Boolean);
  const surname = tokens.length > 1 ? tokens[tokens.length - 1] : tokens[0];
  return surname.slice(0, 2).toUpperCase();
}

// ---------------------------------------------------------------------------
// Empty state — the loud one
// ---------------------------------------------------------------------------

function EmptySlate({ compact }: { compact: boolean }) {
  return (
    <div
      data-testid="slate-chip-empty"
      className={
        'flex items-center gap-2 rounded-lg border border-status-warning ' +
        'bg-status-warning-bg/30 text-status-warning min-w-0 ' +
        (compact ? 'px-2 py-1.5' : 'px-3 py-2')
      }
    >
      <span aria-hidden="true" className="shrink-0">
        ⚠
      </span>
      <span className={'min-w-0 ' + (compact ? 'text-xs' : 'text-sm')}>
        {/* Never abbreviate this to "No candidates" — the second half is the
            part that tells the volunteer what to DO about it. */}
        <strong className="font-semibold">No candidates assigned</strong>
        <span className="text-status-warning/90">
          {' '}
          — check with your coordinator
        </span>
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Expanded detail
// ---------------------------------------------------------------------------

function SlateDetailList({ candidates }: { candidates: CandidateBrief[] }) {
  return (
    <ul className="space-y-2">
      {candidates.map((c) => (
        <li key={c.id} className="flex items-start gap-2 min-w-0">
          <span
            aria-hidden="true"
            className={
              'mt-1.5 h-2 w-2 shrink-0 rounded-full ' + partyDotClass(c.party)
            }
          />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-fg-primary truncate">
              {c.full_name}
            </div>
            <div className="text-xs text-fg-muted truncate">
              {c.office}
              {c.party ? ` · ${c.party}` : ''}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// SlateChip
// ---------------------------------------------------------------------------

export interface SlateChipProps {
  /**
   * The slate, straight off `ShiftBriefing.candidates`. `[]` renders the loud
   * empty state.
   *
   * `null`/`undefined` means "the briefing hasn't loaded yet" and is the ONLY
   * case that renders nothing — distinguishing not-yet-loaded from
   * known-to-be-empty is the whole point, and collapsing them is how the empty
   * state silently disappears.
   */
  candidates: CandidateBrief[] | null | undefined;
  /**
   * `ShiftBriefing.slate_configured` — does this tenant have ANY active
   * candidate?
   *
   * This is what makes the loud empty state trustworthy. An org that hasn't
   * adopted candidates has zero of them, so EVERY route derives an empty slate;
   * shouting "no candidates assigned" at every volunteer on every walk would be
   * a false alarm on every screen, and an always-on warning is one nobody reads
   * when it finally means something.
   *
   * false → the feature is unused; render nothing.
   * true + `[]` → this route really did resolve nobody; say so loudly.
   */
  slateConfigured?: boolean;
  /** Force the compact (initials + count) form regardless of viewport. */
  compact?: boolean;
  className?: string;
}

export function SlateChip({
  candidates,
  slateConfigured = true,
  compact,
  className,
}: SlateChipProps) {
  // Structural, not cosmetic: narrow renders initials, wide renders names.
  const isNarrow = useMediaQuery('(max-width: 767px)');
  const useCompact = compact ?? isNarrow;

  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Dismiss the expanded sheet on outside-tap / Escape. A canvasser's next
  // action after reading the slate is tapping a door, and a panel that ate
  // that tap would be worse than no panel.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Not loaded yet — the one silent case. See the prop docs.
  if (candidates == null) return null;

  // The tenant has no candidates at all — the feature is unused, nothing is
  // missing, and there is nothing to warn about. See the prop docs.
  if (candidates.length === 0 && !slateConfigured) return null;

  if (candidates.length === 0) {
    return (
      <div className={className} data-testid="slate-chip">
        <EmptySlate compact={useCompact} />
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className={'relative min-w-0 ' + (className || '')}
      data-testid="slate-chip"
      data-tour="slate-chip"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={`Knocking for ${candidates
          .map((c) => shortName(c))
          .join(', ')}. Tap for detail.`}
        className={
          'flex w-full min-w-0 items-center gap-2 rounded-lg border ' +
          'border-border-base bg-bg-raised hover:border-accent/60 ' +
          'transition-colors ' +
          (useCompact ? 'px-2 py-1.5' : 'px-3 py-2')
        }
      >
        <span className="text-[10px] uppercase tracking-wide text-fg-muted shrink-0">
          For
        </span>

        {useCompact ? (
          // 375px: initials + count. Three "Name — Office" pairs do not fit.
          <span className="flex min-w-0 items-center gap-1.5">
            {candidates.slice(0, 4).map((c) => (
              <span
                key={c.id}
                className="flex items-center gap-1 shrink-0"
                title={`${c.full_name} — ${c.office}`}
              >
                <span
                  aria-hidden="true"
                  className={
                    'h-1.5 w-1.5 rounded-full ' + partyDotClass(c.party)
                  }
                />
                <span className="text-xs font-bold text-fg-primary">
                  {initialsOf(c)}
                </span>
              </span>
            ))}
            {candidates.length > 4 && (
              <span className="text-xs text-fg-muted shrink-0">
                +{candidates.length - 4}
              </span>
            )}
            <span className="text-xs text-fg-muted shrink-0">
              · {candidates.length}
            </span>
          </span>
        ) : (
          // Desktop: the names themselves. `min-w-0` + `truncate` is the
          // AppShell header truncation guard — without it a long slate pushes
          // its siblings off the row instead of ellipsing itself.
          <span className="min-w-0 truncate text-sm font-semibold text-fg-primary">
            {candidates.map((c) => shortName(c)).join(' · ')}
          </span>
        )}

        <span
          aria-hidden="true"
          className="ml-auto shrink-0 text-xs text-fg-muted"
        >
          {open ? '▲' : '▼'}
        </span>
      </button>

      {open && (
        // Overlay, not an inline grow: a chip that changes its own width on
        // expand is how the header truncation bug comes back.
        <div
          className={
            'absolute right-0 z-30 mt-1 w-64 max-w-[calc(100vw-2rem)] ' +
            'rounded-lg border border-border-base bg-bg-elevated p-3 shadow-lg'
          }
          data-testid="slate-chip-detail"
        >
          <div className="mb-2 text-[10px] uppercase tracking-wide text-fg-muted">
            Who you're knocking for
          </div>
          <SlateDetailList candidates={candidates} />
        </div>
      )}
    </div>
  );
}

export default SlateChip;
